import { apiFetch } from '@/lib/api/client';
import { getSessionFamilyId } from '@/lib/api/session-family';
import type {
  CurrentUser,
  ForgotPasswordInput,
  LoginInput,
  LoginResult,
  RegisterInput,
  ResetPasswordInput,
  SessionSummary,
} from '@/features/auth/types';

interface LoginResponseUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly kind: CurrentUser['kind'];
  readonly platformMemberships: CurrentUser['platformMemberships'];
  readonly organizationMemberships: CurrentUser['organizationMemberships'];
}

interface LoginResponse {
  readonly user: LoginResponseUser;
  readonly accessToken: string;
  readonly accessTokenExpiresInSeconds: number;
  readonly refreshToken: string;
  readonly refreshTokenExpiresAt: string;
  readonly sessionFamilyId: string;
}

/**
 * `POST /auth/login`. The response also carries `refreshToken` (ADR-018,
 * Decision 4), returned here only so `session.tsx` can hold it in memory
 * for the duration of the current page load - it is deliberately never
 * persisted to any storage (ADR-026, Decision F3's spirit: minimize what
 * is kept, applied to the longer-lived, more sensitive refresh token even
 * though F3 names the access token explicitly).
 */
export async function login(input: LoginInput): Promise<LoginResult & { refreshToken: string }> {
  const response = await apiFetch<LoginResponse>('/auth/login', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });

  return {
    user: {
      userId: response.user.id,
      email: response.user.email,
      fullName: response.user.fullName,
      kind: response.user.kind,
      platformMemberships: response.user.platformMemberships,
      organizationMemberships: response.user.organizationMemberships,
    },
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    sessionFamilyId: response.sessionFamilyId,
  };
}

/** `POST /auth/register`. Registration alone does not authenticate the caller. */
export async function register(input: RegisterInput): Promise<void> {
  await apiFetch<unknown>('/auth/register', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });
}

/** `GET /auth/me` - re-resolves the identity for the currently-held access token. */
export async function getCurrentUser(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/me');
}

/**
 * `POST /auth/logout`. Requires the refresh token issued at login
 * (`logoutSchema`), which is only ever held in memory (see `login` above) -
 * if the caller has none (e.g. the access token was restored from
 * `sessionStorage` after a reload, so no in-memory refresh token exists),
 * the caller should skip this network call entirely and just clear local
 * session state; there is nothing valid to send.
 */
export async function logout(refreshToken: string): Promise<void> {
  await apiFetch<unknown>('/auth/logout', {
    method: 'POST',
    body: { refreshToken },
  });
}

/**
 * `POST /auth/forgot-password`. The backend deliberately returns the exact
 * same envelope whether or not the email is registered (ADR-018, Decision
 * 11) - this function passes that response through unchanged rather than
 * inspecting it, so the frontend cannot introduce an enumeration signal the
 * backend already avoided.
 */
export async function forgotPassword(input: ForgotPasswordInput): Promise<void> {
  await apiFetch<unknown>('/auth/forgot-password', { method: 'POST', body: input, skipAuth: true });
}

/** `POST /auth/reset-password`. Single-use token; also revokes every session for the account. */
export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await apiFetch<unknown>('/auth/reset-password', { method: 'POST', body: input, skipAuth: true });
}

/** `POST /auth/verify-email`. Single-use token. */
export async function verifyEmail(token: string): Promise<void> {
  await apiFetch<unknown>('/auth/verify-email', {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

interface SessionSummaryResponse {
  readonly id: string;
  readonly familyId: string;
  readonly deviceLabel: string | null;
  readonly ipAddress: string | null;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly isCurrent: boolean;
}

/**
 * `GET /auth/sessions`. Sends the caller's own session family id
 * (persisted at login, `lib/api/session-family.ts`) so the backend can mark
 * the matching row `isCurrent: true` - the backend has no other way to know
 * which of a user's several active sessions is this one, since the access
 * token itself carries no session data (ADR-018, Decision 3).
 */
export async function listSessions(): Promise<readonly SessionSummary[]> {
  const familyId = getSessionFamilyId();
  const response = await apiFetch<SessionSummaryResponse[]>('/auth/sessions', {
    ...(familyId !== null && { headers: { 'x-session-family-id': familyId } }),
  });
  return response;
}

/** `DELETE /auth/sessions/:familyId`. Actor-scoped server-side: only ever revokes the caller's own session. */
export async function revokeSession(familyId: string): Promise<void> {
  await apiFetch<unknown>(`/auth/sessions/${encodeURIComponent(familyId)}`, { method: 'DELETE' });
}

/**
 * `DELETE /auth/sessions/others`. Requires the caller's own session family
 * id so the backend knows which one to exclude - without it, the backend
 * cannot tell "every other session" from "every session including this one".
 */
export async function revokeOtherSessions(): Promise<void> {
  const familyId = getSessionFamilyId();
  if (familyId === null) {
    // No current-session id to preserve (e.g. this tab restored its access
    // token from sessionStorage after a reload with no login in this page
    // load - see session.tsx). Refuse client-side rather than sending a
    // request the backend would reject anyway with no exclusion applied.
    throw new Error('Sesi saat ini tidak dapat diidentifikasi. Coba masuk ulang.');
  }
  await apiFetch<unknown>('/auth/sessions/others', {
    method: 'DELETE',
    headers: { 'x-session-family-id': familyId },
  });
}
